import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Lock, Building2, Shield, Eye, EyeOff } from 'lucide-react';
import { api } from '../../lib/api';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string, newUser?: any) => void;
  restaurants?: Array<{ id: string; name: string }>;
  mode?: 'add' | 'edit';
  initialData?: any;
}

export function AddUserModal({
  isOpen,
  onClose,
  onSuccess,
  restaurants = [],
  mode = 'add',
  initialData = null,
}: AddUserModalProps) {
  const normalizeRoleCode = (role: string) => String(role || '').trim().toUpperCase().replace(/[\s-]+/g, '_');

  const normalizeRoleForForm = (role: string) => {
    return normalizeRoleCode(role) || 'CUSTOMER';
  };

  const getInitialFormData = (data?: any) => ({
    name: data?.name || '',
    email: data?.email || '',
    phone: data?.phone || '',
    password: '',
    confirmPassword: '',
    role: normalizeRoleForForm(data?.role || data?.roleCode || data?.role_code || data?.roleName),
    restaurantId: data?.restaurantId || data?.restaurant_id || data?.restaurant?.id || '',
    isActive: typeof data?.isActive === 'boolean'
      ? data.isActive
      : typeof data?.is_active === 'boolean'
        ? data.is_active
        : true,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'CUSTOMER',
    restaurantId: '',
    isActive: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState<Array<{
    value: string;
    label: string;
    icon: any;
    description: string;
  }>>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const getFallbackRoles = () => {
    const defaults = [
      { code: 'SUPER_ADMIN', name: 'Super Admin', description: 'Full platform access and system configuration' },
      { code: 'RESTAURANT_ADMIN', name: 'Restaurant Admin', description: 'Manage restaurant operations and staff' },
      { code: 'MANAGER', name: 'Manager', description: 'Oversee daily operations and team workflow' },
      { code: 'KITCHEN_STAFF', name: 'Kitchen Staff', description: 'Handle kitchen preparation and order readiness' },
      { code: 'CUSTOMER', name: 'Customer', description: 'End user account for ordering and tracking' },
    ];

    return defaults.map((role) => ({
      value: normalizeRoleCode(role.code),
      label: role.name,
      icon: mapRoleIcon(role.code),
      description: role.description,
    }));
  };

  const mapRoleIcon = (roleCode: string) => {
    const normalized = normalizeRoleCode(roleCode);
    if (normalized === 'SUPER_ADMIN' || normalized === 'KITCHEN_STAFF' || normalized === 'STAFF') {
      return Shield;
    }
    if (normalized === 'MANAGER' || normalized === 'RESTAURANT_ADMIN') {
      return Building2;
    }
    return User;
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData(getInitialFormData(initialData));
      } else {
        setFormData(getInitialFormData());
      }
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen, mode, initialData]);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const response = await api.masterData.userRoles.getAll();
        const sourceRows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray((response as any)?.data?.roles)
            ? (response as any).data.roles
            : Array.isArray((response as any)?.roles)
              ? (response as any).roles
              : [];

        const roleRows = sourceRows
          .filter((r: any) => r.isActive !== false)
          .map((role: any) => ({
            value: normalizeRoleCode(role.code || role.role_code || role.name),
            label: role.name || role.role_name || role.code,
            icon: mapRoleIcon(role.code || role.role_code || ''),
            description: role.description || '',
          }))
          .filter((role: any) => role.value && role.label);

        const finalRoles = roleRows.length > 0 ? roleRows : getFallbackRoles();

        if (mounted) {
          setRoles(finalRoles);
          if (finalRoles.length > 0 && !finalRoles.some((r: any) => r.value === formData.role)) {
            setFormData((prev) => ({ ...prev, role: finalRoles[0].value }));
          }
        }
      } catch (_error) {
        if (mounted) {
          const fallbackRoles = getFallbackRoles();
          setRoles(fallbackRoles);
          if (fallbackRoles.length > 0 && !fallbackRoles.some((r: any) => r.value === formData.role)) {
            setFormData((prev) => ({ ...prev, role: fallbackRoles[0].value }));
          }
          setErrors((prev) => {
            const next = { ...prev };
            if (next.submit === 'Failed to load roles from master data') {
              delete next.submit;
            }
            return next;
          });
        }
      } finally {
        if (mounted) setRolesLoading(false);
      }
    };

    loadRoles();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation (optional but if provided, validate)
    if (formData.phone && !/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    // Password validation
    if (mode === 'add') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else {
      // In edit mode, password is optional
      if (formData.password && formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (formData.password && formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Restaurant validation for non-customer/superadmin roles
    if (['STAFF', 'KITCHEN_STAFF', 'MANAGER', 'RESTAURANT_ADMIN'].includes(normalizeRoleCode(formData.role)) && !formData.restaurantId) {
      newErrors.restaurantId = 'Restaurant is required for this role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const userData: any = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        isActive: formData.isActive,
      };

      if (mode === 'add') {
        userData.password = formData.password;
      }

      // Add optional fields
      if (formData.phone) {
        userData.phone = formData.phone;
      }

      if (formData.restaurantId) {
        userData.restaurantId = formData.restaurantId;
      }

      const response = (mode === 'edit' && initialData?.id
        ? await api.users.update(initialData.id, userData)
        : await api.users.create(userData)) as any;

      if (response.success) {
        onSuccess(
          response?.message || (mode === 'edit' ? 'User updated successfully!' : 'User created successfully!'),
          response?.data
        );
        onClose();
      } else {
        setErrors({ submit: `Failed to ${mode === 'edit' ? 'update' : 'create'} user. Please try again.` });
      }
    } catch (error: any) {
      setErrors({ 
        submit: error.message || 'Unable to connect to server. Please check if the backend is running or try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  const selectedRole = roles.find(r => r.value === formData.role);
  const requiresRestaurant = ['STAFF', 'KITCHEN_STAFF', 'MANAGER', 'RESTAURANT_ADMIN'].includes(normalizeRoleCode(formData.role));

  return (
    <div className="fixed inset-0 bg-white/10 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-gray-900 font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              {mode === 'edit' ? 'Edit User' : 'Add New User'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {mode === 'edit'
                ? 'Update user account details and permissions'
                : 'Create a new user account with specific role and permissions'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {errors.submit}
            </div>
          )}

          {/* Personal Information */}
          <div>
            <h3 className="text-gray-900 font-medium mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                      errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter full name"
                  />
                </div>
                {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                      errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="user@example.com"
                  />
                </div>
                {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                      errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
              </div>

              {mode === 'add' && (
                <>
                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`w-full pl-10 pr-12 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Min. 6 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={`w-full pl-10 pr-12 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Re-enter password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Role & Permissions */}
          <div>
            <h3 className="text-gray-900 font-medium mb-4">Role & Permissions</h3>
            
            {/* Role Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                User Role <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 gap-3">
                {rolesLoading && (
                  <div className="text-sm text-gray-500">Loading roles...</div>
                )}
                {roles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <label
                      key={role.value}
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.role === role.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={formData.role === role.value}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-5 h-5 ${formData.role === role.value ? 'text-purple-600' : 'text-gray-400'}`} />
                          <span className="font-medium text-gray-900">{role.label}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Restaurant Selection (conditional) */}
            {requiresRestaurant && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to Restaurant <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={formData.restaurantId}
                    onChange={(e) => handleInputChange('restaurantId', e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none ${
                      errors.restaurantId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a restaurant</option>
                    {restaurants.map((restaurant) => (
                      <option key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.restaurantId && <p className="text-red-600 text-sm mt-1">{errors.restaurantId}</p>}
                {restaurants.length === 0 && (
                  <p className="text-amber-600 text-sm mt-1">⚠️ No restaurants available. Please create a restaurant first.</p>
                )}
              </div>
            )}

            {/* Active Status */}
            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Active Account</span>
                  <p className="text-xs text-gray-500">User can login and access the system</p>
                </div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {mode === 'edit' ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <User className="w-4 h-4" />
                  {mode === 'edit' ? 'Save Changes' : 'Create User'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
